/**
 * Per-canvas undo/redo history for `WorkspacePageV2` (T289182224).
 *
 * `WorkspacePageV2` stays MOUNTED across canvas tab switches — `App.tsx` routes
 * `/project/:projectId/workspace/:canvasId` with no `key`, so only the param
 * changes. An undo entry is a whole-graph ReactFlow snapshot, and `handleUndo`
 * replays one through `syncStateDiffToBackend`, which `removeNodeFromYDoc()`s
 * every node on the CURRENT canvas that the snapshot does not contain. A single
 * stack shared by every canvas therefore lets one Cmd+Z after a tab switch
 * overwrite the open canvas with a sibling canvas's graph — that is
 * T288688966 / Workplace post 1620433736380590, where
 * `affordable-fashion-finds-a547856b/canvas-3ff59750` lost its own 10 nodes and
 * gained 66 from `canvas-fda117d2` in a single flush.
 *
 * #3687 stopped that by emptying both stacks on every canvas switch. Correct,
 * but it also threw away the history of the canvas the user comes back to, so
 * undo became a silent no-op after a switch — T289182224. History is BUCKETED
 * per room key here instead: switching away PARKS a canvas's history and
 * switching back RESTORES it.
 *
 * Parking is only safe if we can tell that the canvas did not change while the
 * user was away, so three independent gates sit in front of a replay. Each
 * exists because an earlier, simpler design failed adversarial review:
 *
 * 1. ROOM STAMP. Every entry records the room it was captured on, and the
 *    mutating calls take the caller's own idea of the current room as an
 *    argument. The store's active room is moved by the page's `[canvasRoomKey]`
 *    layout effect; the argument comes from the render scope of the handler
 *    about to write to YDoc. When they disagree the store is stale relative to
 *    the canvas on screen — exactly when replaying destroys nodes — so the
 *    entry is refused. Asymmetric on purpose: `record` realigns on a mismatch
 *    (filing a snapshot is never destructive, and dropping it is how undo
 *    silently stops working) while `peekUndo`/`peekRedo` never do.
 *
 * 2. ROOM EPOCH. The server's `room_epoch` — the authoritative CRDT generation,
 *    already used by `canvasRoomPool` to force a reload when it moves. History
 *    from a previous generation cannot be replayed onto a forked document.
 *    Note this is deliberately NOT the identity of the local `Y.Doc` object:
 *    the pool mints a fresh `Y.Doc` on ordinary idle eviction, which is not a
 *    fork, so an object-identity fence would silently discard good history five
 *    minutes after parking — the reported bug back, on a timer.
 *
 * 3. CHANGED-WHILE-PARKED FINGERPRINT ({@link fingerprintGraph}). Parking a
 *    history means it can be replayed against a canvas that moved on without
 *    it. So a room's live graph is fingerprinted when it is parked and again
 *    when it is restored; a difference drops that room's history. The
 *    fingerprint covers exactly the fields a replay can mutate and nothing
 *    else, so a generation completing on a parked canvas does not cost the user
 *    their undo history, while a node, edge, position, parent or group
 *    membership changing does.
 *
 * Evaluating the fingerprint at the park/restore BOUNDARY rather than per
 * replay is what makes it safe: an earlier design that classified individual
 * entities as "foreign" at replay time refused to undo a freshly created edge,
 * paste or duplicate, because those record BEFORE minting their ids.
 *
 * The module is deliberately free of React, Yjs and canvas-v2 imports so the
 * invariants above can be tested directly rather than by scanning source text.
 */

/** Cap on entries kept per room. Matches the historical global `MAX_UNDO`. */
export const MAX_UNDO_ENTRIES = 50

/**
 * How many rooms keep history at once. Each entry is a whole-graph snapshot and
 * a room can hold {@link MAX_UNDO_ENTRIES} of them, so an unbounded map over a
 * project's canvases is a real leak. Three covers the flip-between-two-tabs
 * workflow the bug report describes, with a spare.
 */
export const MAX_TRACKED_ROOMS = 3

/**
 * How many rooms keep a remembered reason for losing their history.
 *
 * Far larger than {@link MAX_TRACKED_ROOMS}, deliberately: a reason has to
 * outlive the bucket it explains, since LRU eviction is itself one of the
 * reasons. Each entry is a room key and a short enum, so this bound is a
 * backstop against an unbounded map in a marathon session rather than
 * something a real one reaches.
 */
export const MAX_REMEMBERED_DROP_REASONS = 200

/** The single place the `${projectId}/${canvasId}` room key shape is defined. */
export function canvasRoomKeyOf(projectId: string, canvasId: string): string {
  // Fail loud rather than minting a shadow room. An empty project id used to
  // yield `/agent`, a perfectly valid-looking bucket key that no real canvas
  // ever writes to, so history filed under it would simply vanish.
  if (!projectId) throw new Error('canvasRoomKeyOf: empty projectId')
  if (!canvasId) throw new Error('canvasRoomKeyOf: empty canvasId')
  return `${projectId}/${canvasId}`
}

/**
 * The server's canvas generation. `null` while unknown — before the room state
 * has been fetched, and for the placeholder document `useYDoc` hands back
 * before a room is acquired. History is never recorded or replayed against an
 * unknown epoch.
 */
export type RoomEpoch = number | null

/**
 * Minimal node shape the fingerprint needs. Keeps this module ReactFlow-free.
 *
 * Group membership arrives already extracted rather than as a `data` blob: a
 * ReactFlow node's `data` is a union over every generated node type, and
 * reaching into it here would mean a cast in a module whose whole point is to
 * be independently testable.
 */
export type FingerprintNode = {
  readonly id: string
  readonly position?: { readonly x: number; readonly y: number }
  readonly parentId?: string | null
  /** `in_child_node_ids`, or null when the node has no membership field. */
  readonly childNodeIds?: readonly string[] | null
}

/**
 * Minimal edge shape the fingerprint needs.
 *
 * Keyed on the PORT TUPLE, never on `edge.id` — see {@link edgeReplayKey}.
 */
export type FingerprintEdge = {
  readonly source: string
  readonly sourceHandle?: string | null
  readonly target: string
  readonly targetHandle?: string | null
  readonly data?: { readonly fromIndex?: number; readonly toIndex?: number } | null
}

/**
 * Identity of an edge AS A REPLAY SEES IT.
 *
 * `syncStateDiffToBackend` diffs edges on this tuple and not on `edge.id`,
 * because ids come from several non-deterministic sources (`paste-${ts}`,
 * `dup-${ts}`, `${source}-${target}-${ts}`, auto) while the YDoc edge id is
 * deterministic from the ports. The page imports this helper so the diff and
 * the fingerprint cannot drift apart.
 *
 * Fingerprinting ids instead would fail both ways: a Director change to
 * `targetHandle` or `toIndex` keeps the id and would slip through, and a
 * pasted edge's `paste-edge-*` id is replaced by the canonical one on a cold
 * rebuild, so an id-based fingerprint would report a change where the graph is
 * semantically identical and throw away history the user still needs.
 */
export function edgeReplayKey(edge: FingerprintEdge): string {
  const fromIndex = edge.data?.fromIndex ?? ''
  const toIndex = edge.data?.toIndex ?? ''
  return [
    edge.source,
    edge.sourceHandle ?? '',
    edge.target,
    edge.targetHandle ?? '',
    String(fromIndex),
    String(toIndex),
  ].join('|')
}

/**
 * A stable digest of everything a replay can MUTATE, and nothing else.
 *
 * In: node ids, positions, `parentId`, `in_child_node_ids`, and every edge's
 * replay key. Out: node data at large — status, `out_image_path`, prompts.
 * `syncStateDiffToBackend` never restores general data onto an existing node,
 * so that churn cannot be destroyed by a replay; including it would drop a
 * user's history every time a generation finished on a canvas they were not
 * looking at, which is the very bug under repair.
 *
 * Sorted, so ordering differences between a live graph and a rebuilt one do not
 * read as a change.
 */
export function fingerprintGraph(
  nodes: readonly FingerprintNode[],
  edges: readonly FingerprintEdge[],
): string {
  const nodeParts = nodes
    .map((node) => {
      const x = node.position?.x ?? 0
      const y = node.position?.y ?? 0
      // JSON so an array of child ids compares structurally; a node with no
      // membership field contributes the empty string.
      const children = node.childNodeIds
      const childrenPart = children == null ? '' : JSON.stringify(children)
      return `${node.id}|${x}|${y}|${node.parentId ?? ''}|${childrenPart}`
    })
    .sort()
  const edgeParts = edges.map(edgeReplayKey).sort()
  return `n:${nodeParts.join(';')}\ne:${edgeParts.join(';')}`
}

/** A whole-graph snapshot, before it is attributed to a room. */
export type UndoGraphSnapshot<TNode, TEdge> = {
  readonly nodes: TNode[]
  readonly edges: TEdge[]
}

/** A snapshot stamped with the room and epoch it was recorded on. */
export type RoomStampedSnapshot<TNode, TEdge> = UndoGraphSnapshot<TNode, TEdge> & {
  readonly roomKey: string
  readonly epoch: RoomEpoch
}

/**
 * A candidate handed back by `peekUndo`/`peekRedo`.
 *
 * Peek does NOT mutate. The caller applies the snapshot and then calls
 * {@link UndoHistory.commit}, which verifies this is still the stack head
 * before moving it. The two-step exists because the previous shape popped the
 * entry and pushed the replacement BEFORE the caller's own guards had run, so a
 * late refusal left a spurious redo entry and a lost undo entry with nothing
 * applied.
 */
export type UndoCandidate<TNode, TEdge> = {
  readonly direction: 'undo' | 'redo'
  readonly roomKey: string
  readonly snapshot: RoomStampedSnapshot<TNode, TEdge>
}

/** NAD trace records (`dw:undo-canvas-scope:switch`). */
export type UndoHistoryTraceEvent = {
  event:
    | 'history-parked-on-canvas-switch'
    | 'history-restored-on-canvas-switch'
    | 'history-evicted-idle-canvas'
    | 'history-realigned-to-caller-room'
    | 'history-room-mismatch-refused'
    | 'history-dropped-stale-epoch'
    | 'history-dropped-changed-while-parked'
    | 'history-commit-stale-candidate'
  recordedOnCanvasRoomKey: string
  nowOnCanvasRoomKey: string
  undoEntries: number
  redoEntries: number
  evictedCanvasRoomKeys?: string[]
  direction?: 'undo' | 'redo'
}

/**
 * Is `snapshot` safe to replay onto `roomKey` at `epoch`?
 *
 * Exported because the guard is applied twice on purpose — once inside the
 * store when a candidate is peeked, and once in `WorkspacePageV2`'s handlers
 * immediately before the snapshot is handed to `syncStateDiffToBackend`, the
 * call that actually deletes nodes. One check is a store invariant; the second
 * sits on the destructive boundary itself. Because peek no longer mutates, a
 * refusal by the second check costs nothing.
 */
export function isSnapshotReplayable<TNode, TEdge>(
  snapshot: RoomStampedSnapshot<TNode, TEdge>,
  roomKey: string,
  epoch: RoomEpoch,
): boolean {
  if (snapshot.roomKey !== roomKey) return false
  // An unknown epoch on either side is never replayable: it means the room
  // state has not been fetched, so we cannot know which generation we are on.
  if (snapshot.epoch === null || epoch === null) return false
  return snapshot.epoch === epoch
}

/**
 * Why a room's history is gone, when it did not simply run out.
 *
 * Kept so the page can TELL the user. A dropped history and an empty one are
 * indistinguishable at the moment Cmd+Z does nothing, and "undo did nothing" is
 * precisely the complaint T289182224 was filed about — so a refusal that cannot
 * explain itself reads to the user as the bug coming back.
 */
export type UndoHistoryDropReason = 'changed-while-parked' | 'stale-epoch' | 'evicted'

/**
 * Why a replay did not happen, in the vocabulary the user needs.
 *
 * The two drop reasons explain a history that is GONE; the other two explain a
 * history that is present but momentarily unusable. `null` means it ran.
 */
export type ReplayRefusal = UndoHistoryDropReason | 'canvas-loading' | 'not-replayable'

/**
 * The `pages` i18n key that explains a refusal.
 *
 * A pure function rather than a ternary inside the component, so the MAPPING
 * can be tested. A test that only checks "all four key spellings appear in the
 * source" stays green if two of them are swapped, and the user is then told the
 * wrong thing with complete confidence.
 */
export function refusalMessageKey(refusal: ReplayRefusal): string {
  switch (refusal) {
    case 'changed-while-parked':
      return 'undoHistory.clearedCanvasChanged'
    case 'stale-epoch':
      return 'undoHistory.clearedNewVersion'
    case 'evicted':
      return 'undoHistory.clearedTooManyCanvases'
    case 'canvas-loading':
      return 'undoHistory.canvasLoading'
    case 'not-replayable':
      return 'undoHistory.unavailable'
  }
}

export type UndoHistory<TNode, TEdge> = {
  /** Room the history is currently reading from and recording into. */
  activeRoomKey(): string
  /**
   * Why `roomKey`'s history was discarded, or `null` if it was not — including
   * after the user records something new, which makes the old reason stale.
   */
  lastDropReason(roomKey: string): UndoHistoryDropReason | null
  /**
   * Park the active room's history and make `roomKey` the active bucket.
   *
   * `epoch` is the incoming room's generation and `liveFingerprint` its current
   * graph digest, or `null` when either is not yet knowable — in which case the
   * incoming room's history stays parked and is validated on a later call. The
   * page therefore calls this both when the room key changes and when the
   * canvas becomes interactive.
   */
  switchToRoom(roomKey: string, epoch: RoomEpoch, liveFingerprint: string | null): void
  /**
   * Note the outgoing room's graph digest so the next restore can tell whether
   * the canvas changed while parked. Called with the room being LEFT, while its
   * graph is still the one in the page's refs.
   */
  noteLiveFingerprint(roomKey: string, fingerprint: string): void
  /**
   * Push a snapshot of the pre-change graph and drop that room's redo branch.
   * Realigns the active room first if the store lags the caller. A record on an
   * unknown epoch is DROPPED rather than filed: it could never be replayed, and
   * filing it would clear a redo branch that is still good.
   */
  record(roomKey: string, epoch: RoomEpoch, snapshot: UndoGraphSnapshot<TNode, TEdge>): void
  /** Non-mutating look at the newest undo entry. `null` when unusable. */
  peekUndo(roomKey: string, epoch: RoomEpoch): UndoCandidate<TNode, TEdge> | null
  /** Mirror of {@link peekUndo} in the redo direction. */
  peekRedo(roomKey: string, epoch: RoomEpoch): UndoCandidate<TNode, TEdge> | null
  /**
   * Move a peeked candidate across, pushing `current` onto the other stack.
   * Refuses (and traces) if the candidate is no longer the stack head.
   */
  commit(
    candidate: UndoCandidate<TNode, TEdge>,
    current: UndoGraphSnapshot<TNode, TEdge>,
  ): boolean
  /** Entry counts for the given room (defaults to the active one). */
  depth(roomKey?: string): { undo: number; redo: number }
  /** Rooms currently holding history, least-recently-active first. */
  trackedRoomKeys(): string[]
}

type RoomHistory<TNode, TEdge> = {
  undo: RoomStampedSnapshot<TNode, TEdge>[]
  redo: RoomStampedSnapshot<TNode, TEdge>[]
  /** Epoch every entry in this bucket was recorded on. */
  epoch: RoomEpoch
  /** Graph digest as of the last time this room was live. */
  fingerprint: string | null
}

/**
 * Create a per-room undo/redo history.
 *
 * @param trace structured NAD logger, so a trace can still say WHICH canvas a
 *   history belonged to and WHY it went away — the question #3687's log existed
 *   to answer, kept answerable now that history is parked rather than dropped.
 */
export function createUndoHistory<TNode, TEdge>(
  trace: (record: UndoHistoryTraceEvent) => void,
): UndoHistory<TNode, TEdge> {
  // Insertion order doubles as LRU order: least recently activated room first,
  // active room always last.
  const rooms = new Map<string, RoomHistory<TNode, TEdge>>()
  // Why a room's history went away, kept OUTSIDE the buckets because LRU
  // eviction deletes the bucket entirely — and an evicted history is exactly
  // the case where the user gets a silent Cmd+Z with no way to know why.
  // Bounded, but generously. An entry is a room key plus a short enum — a few
  // dozen bytes — so the cap is here to stop unbounded growth over a very long
  // session, NOT to be reached in a normal one. Sizing it to a small multiple
  // of the tracked-room limit was wrong: a user editing sixteen canvases would
  // trim the first room's reason while it was still the only explanation for
  // that room's silent Cmd+Z, which is the bug this map exists to prevent.
  const dropReasons = new Map<string, UndoHistoryDropReason>()
  let activeRoomKey = ''

  function noteDropReason(roomKey: string, reason: UndoHistoryDropReason): void {
    dropReasons.delete(roomKey)
    dropReasons.set(roomKey, reason)
    while (dropReasons.size > MAX_REMEMBERED_DROP_REASONS) {
      const oldest = dropReasons.keys().next()
      if (oldest.done) break
      dropReasons.delete(oldest.value)
    }
  }

  function isEmpty(room: RoomHistory<TNode, TEdge>): boolean {
    return room.undo.length === 0 && room.redo.length === 0
  }

  function clearHistory(
    roomKey: string,
    room: RoomHistory<TNode, TEdge>,
    reason: UndoHistoryDropReason,
  ): void {
    room.undo = []
    room.redo = []
    room.epoch = null
    noteDropReason(roomKey, reason)
  }

  function evictIdleRooms(): string[] {
    const evicted: string[] = []
    for (const roomKey of Array.from(rooms.keys())) {
      if (rooms.size <= MAX_TRACKED_ROOMS) break
      if (roomKey === activeRoomKey) continue
      const losing = rooms.get(roomKey)
      if (losing && !isEmpty(losing)) noteDropReason(roomKey, 'evicted')
      rooms.delete(roomKey)
      evicted.push(roomKey)
    }
    return evicted
  }

  function push(
    stack: RoomStampedSnapshot<TNode, TEdge>[],
    snapshot: UndoGraphSnapshot<TNode, TEdge>,
    roomKey: string,
    epoch: RoomEpoch,
  ): void {
    stack.push({ roomKey, epoch, nodes: snapshot.nodes, edges: snapshot.edges })
    if (stack.length > MAX_UNDO_ENTRIES) stack.shift()
  }

  function bucketFor(roomKey: string): RoomHistory<TNode, TEdge> {
    const existing = rooms.get(roomKey)
    if (existing) return existing
    const created: RoomHistory<TNode, TEdge> = {
      undo: [],
      redo: [],
      epoch: null,
      fingerprint: null,
    }
    rooms.set(roomKey, created)
    return created
  }

  /**
   * Apply the epoch and changed-while-parked gates to a room that is becoming
   * active again. Either one drops the history outright — there is no partial
   * salvage, because a snapshot is only meaningful against the graph it was
   * taken from.
   */
  function validateOnRestore(
    roomKey: string,
    room: RoomHistory<TNode, TEdge>,
    epoch: RoomEpoch,
    liveFingerprint: string | null,
  ): void {
    if (isEmpty(room)) {
      room.epoch = epoch
      room.fingerprint = liveFingerprint
      return
    }

    if (epoch !== null && room.epoch !== null && room.epoch !== epoch) {
      trace({
        event: 'history-dropped-stale-epoch',
        recordedOnCanvasRoomKey: roomKey,
        nowOnCanvasRoomKey: roomKey,
        undoEntries: room.undo.length,
        redoEntries: room.redo.length,
      })
      clearHistory(roomKey, room, 'stale-epoch')
      room.epoch = epoch
      room.fingerprint = liveFingerprint
      return
    }

    if (
      liveFingerprint !== null &&
      room.fingerprint !== null &&
      room.fingerprint !== liveFingerprint
    ) {
      trace({
        event: 'history-dropped-changed-while-parked',
        recordedOnCanvasRoomKey: roomKey,
        nowOnCanvasRoomKey: roomKey,
        undoEntries: room.undo.length,
        redoEntries: room.redo.length,
      })
      clearHistory(roomKey, room, 'changed-while-parked')
    }

    if (epoch !== null) room.epoch = epoch
    if (liveFingerprint !== null) room.fingerprint = liveFingerprint
  }

  function switchToRoom(
    roomKey: string,
    epoch: RoomEpoch,
    liveFingerprint: string | null,
  ): void {
    if (roomKey === activeRoomKey) {
      // Same room, later information — the canvas has become interactive and
      // can now answer "did anything change while I was away?". Validate, do
      // not re-park.
      validateOnRestore(roomKey, bucketFor(roomKey), epoch, liveFingerprint)
      return
    }

    const leaving = rooms.get(activeRoomKey)
    if (leaving && !isEmpty(leaving)) {
      trace({
        event: 'history-parked-on-canvas-switch',
        recordedOnCanvasRoomKey: activeRoomKey,
        nowOnCanvasRoomKey: roomKey,
        undoEntries: leaving.undo.length,
        redoEntries: leaving.redo.length,
      })
    }

    activeRoomKey = roomKey
    // Delete-then-set moves the room to the end of the LRU order.
    const arriving = rooms.get(roomKey)
    if (arriving) rooms.delete(roomKey)
    const restored: RoomHistory<TNode, TEdge> = arriving ?? {
      undo: [],
      redo: [],
      epoch: null,
      fingerprint: null,
    }
    rooms.set(roomKey, restored)

    const evicted = evictIdleRooms()
    if (evicted.length > 0) {
      trace({
        event: 'history-evicted-idle-canvas',
        recordedOnCanvasRoomKey: evicted.join(','),
        nowOnCanvasRoomKey: roomKey,
        undoEntries: restored.undo.length,
        redoEntries: restored.redo.length,
        evictedCanvasRoomKeys: evicted,
      })
    }

    const hadHistory = !isEmpty(restored)
    validateOnRestore(roomKey, restored, epoch, liveFingerprint)

    if (hadHistory && !isEmpty(restored)) {
      trace({
        event: 'history-restored-on-canvas-switch',
        recordedOnCanvasRoomKey: roomKey,
        nowOnCanvasRoomKey: roomKey,
        undoEntries: restored.undo.length,
        redoEntries: restored.redo.length,
      })
    }
  }

  function peek(
    direction: 'undo' | 'redo',
    roomKey: string,
    epoch: RoomEpoch,
  ): UndoCandidate<TNode, TEdge> | null {
    const room = rooms.get(activeRoomKey)
    if (!room) return null
    const from = direction === 'undo' ? room.undo : room.redo
    const candidate = from[from.length - 1]
    if (!candidate) return null

    if (!isSnapshotReplayable(candidate, roomKey, epoch)) {
      // Either the store's active room is behind the canvas the caller is on —
      // so this entry belongs to a different canvas and replaying it would
      // delete every node the open canvas has and the snapshot lacks
      // (T288688966) — or the generation has moved under it. Refuse.
      //
      // Deliberately NOT cleared on a room mismatch: the entries are valid
      // history for the room they were recorded on, and the next `record`
      // realigns the store. Undo stays a no-op until then, the safe direction.
      trace({
        event: 'history-room-mismatch-refused',
        recordedOnCanvasRoomKey: candidate.roomKey,
        nowOnCanvasRoomKey: roomKey,
        undoEntries: room.undo.length,
        redoEntries: room.redo.length,
        direction,
      })
      return null
    }

    return { direction, roomKey, snapshot: candidate }
  }

  return {
    activeRoomKey() {
      return activeRoomKey
    },

    switchToRoom,

    lastDropReason(roomKey: string) {
      return dropReasons.get(roomKey) ?? null
    },

    noteLiveFingerprint(roomKey: string, fingerprint: string) {
      // Deliberately does NOT create a bucket. A room evicted by the LRU has no
      // history left to protect, and recreating an empty entry for it would
      // occupy a tracked slot and push a room that still HAS history out on the
      // next eviction — losing exactly what this store exists to keep.
      const room = rooms.get(roomKey)
      if (!room) return
      room.fingerprint = fingerprint
    },

    record(roomKey: string, epoch: RoomEpoch, snapshot: UndoGraphSnapshot<TNode, TEdge>) {
      // No epoch means the room state has not been fetched yet. Such an entry
      // could never pass `isSnapshotReplayable`, so filing it would only clear
      // a redo branch that is still perfectly good.
      if (epoch === null) return

      if (roomKey !== activeRoomKey) {
        // The store is lagging the canvas on screen. Filing a snapshot under
        // the right room is always safe, so heal rather than drop the entry —
        // dropping it is how undo silently stops working.
        trace({
          event: 'history-realigned-to-caller-room',
          recordedOnCanvasRoomKey: activeRoomKey,
          nowOnCanvasRoomKey: roomKey,
          undoEntries: rooms.get(activeRoomKey)?.undo.length ?? 0,
          redoEntries: rooms.get(activeRoomKey)?.redo.length ?? 0,
        })
        switchToRoom(roomKey, epoch, null)
      }
      const room = bucketFor(roomKey)
      if (room.epoch !== null && room.epoch !== epoch) {
        // The generation moved while this room was active. Its entries are from
        // the old document; keeping them would let a replay resurrect it.
        trace({
          event: 'history-dropped-stale-epoch',
          recordedOnCanvasRoomKey: roomKey,
          nowOnCanvasRoomKey: roomKey,
          undoEntries: room.undo.length,
          redoEntries: room.redo.length,
        })
        clearHistory(roomKey, room, 'stale-epoch')
      }
      room.epoch = epoch
      // A fresh entry supersedes any earlier explanation: the user has usable
      // history again, so the old reason must not surface on a later Cmd+Z.
      dropReasons.delete(roomKey)
      push(room.undo, snapshot, roomKey, epoch)
      room.redo = []
    },

    peekUndo(roomKey: string, epoch: RoomEpoch) {
      return peek('undo', roomKey, epoch)
    },

    peekRedo(roomKey: string, epoch: RoomEpoch) {
      return peek('redo', roomKey, epoch)
    },

    commit(candidate, current) {
      const room = rooms.get(candidate.roomKey)
      if (!room) return false
      const from = candidate.direction === 'undo' ? room.undo : room.redo
      const onto = candidate.direction === 'undo' ? room.redo : room.undo
      if (from[from.length - 1] !== candidate.snapshot) {
        // Something moved the stack between peek and commit. Do not guess which
        // entry the caller applied — leave both stacks exactly as they are.
        trace({
          event: 'history-commit-stale-candidate',
          recordedOnCanvasRoomKey: candidate.snapshot.roomKey,
          nowOnCanvasRoomKey: candidate.roomKey,
          undoEntries: room.undo.length,
          redoEntries: room.redo.length,
          direction: candidate.direction,
        })
        return false
      }
      from.pop()
      push(onto, current, candidate.roomKey, candidate.snapshot.epoch)
      return true
    },

    depth(roomKey?: string) {
      const room = rooms.get(roomKey ?? activeRoomKey)
      return { undo: room?.undo.length ?? 0, redo: room?.redo.length ?? 0 }
    },

    trackedRoomKeys() {
      return Array.from(rooms.keys())
    },
  }
}
